/**
 * Phase 6 cross-cutting PR 4e — `useSeasonPlans` hook.
 *
 * Fetches ALL training_plans for the active season with embedded
 * `plan_days`. Mirrors `useWeeklyPlan` (which returns only the
 * highest-week_number plan) but the consumer here (`useProactiveChecks`)
 * needs the full multi-week dataset to compute:
 *   - Current consecutive-completed-day streak (back across weeks until
 *     hitting an incomplete non-rest day).
 *   - This-week vs last-week compliance % (compliance_drop trigger).
 *
 * Query shape matches the existing `GET /api/plans?seasonId=X` server
 * route (`api/index.js:2804-2827`) — `training_plans.*, plan_days(*)`
 * scoped to the authed user by RLS. The hook reads directly through
 * Supabase rather than going through `useAuthFetch` because:
 *   1. `useWeeklyPlan` does the same — keeps the data-fetch pattern
 *      consistent across the two hooks that query the same table.
 *   2. RLS handles the user-scoping equivalently to the server's
 *      `eq('user_id', userId)` filter, so no privilege change.
 *
 * Returns `data: null` (not a fixture) when there's no active season,
 * no plans yet (brand-new athlete), or the query fails — the consumer
 * (`computeCurrentStreak` / `computeThisAndLastWeekCompliance`) treats
 * empty input as "0 streak, null compliance," which translates to
 * "no streak_milestone / compliance_drop fire today." Safe no-op.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { dedupePlansByWeekNewest, type SeasonPlanShape } from '../lib/seasonPlanAggregation';

export interface UseSeasonPlansArgs {
  /** Active season id (typically from `useActiveSeason`). When null,
   *  the hook stays idle and returns `data: null`. */
  seasonId: string | null;
}

export interface UseSeasonPlansResult {
  data: SeasonPlanShape[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Row shape returned by the Supabase query. We only depend on the
 * columns the aggregation helpers need; the rest of the row is
 * available for future consumers if we widen `SeasonPlanShape`.
 */
interface SeasonPlanRow extends SeasonPlanShape {
  id: string;
  user_id: string;
  season_id: string;
  status: string | null;
  /** Season-model Phase 1 — newest-wins tiebreaker for dedup. */
  created_at?: string | null;
}

export function useSeasonPlans({ seasonId }: UseSeasonPlansArgs): UseSeasonPlansResult {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;

  const [data, setData] = useState<SeasonPlanShape[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!supabase || !userId || !seasonId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: queryError } = await supabase
        .from('training_plans')
        .select('id, user_id, season_id, week_number, status, created_at, plan_days(day_of_week, workout_type, completion_status, details)')
        .eq('user_id', userId)
        .eq('season_id', seasonId)
        .order('week_number', { ascending: true });
      if (queryError) throw queryError;
      const plans = (rows as SeasonPlanRow[] | null) ?? [];
      // Season-model Phase 1 — keep one row per week_number (newest wins) so
      // streak + compliance don't count the same week 2–6× on legacy data.
      setData(dedupePlansByWeekNewest(plans));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [supabase, userId, seasonId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (!supabase || !userId) {
    return { data: null, loading: false, error: null, refetch: async () => {} };
  }

  return { data, loading, error, refetch };
}
